import { useCallback } from 'react'
import { APIDecoratorWithBaseURI } from '../../Service/api'

export interface EventReview {
  idEventReview: string
  clientName: string
  userRating: number | null
  userComment: string | null
  moderated: boolean
  moderatedReason: string | null
  userRatingAt: string | null
  approvedAt: string | null
  createdAt: string
  url: string
}

export interface EventReviewStatistics {
  answered: number
  percentage: number
  total: number
  avg: number
}

export interface EventReviewsResponse {
  data: EventReview[]
  statistics: EventReviewStatistics
}

export type EditEventReviewAction = 'approved' | 'rejected'

export interface EditEventReviewPayload {
  action: EditEventReviewAction
  moderatedReason?: string
}

export type ExtraQuestionAnswerType = 'single' | 'multiple'

export interface EventExtraQuestionOption {
  idOption?: string
  label: string
  /** Quantidade de avaliações que marcaram esta opção. */
  answersCount?: number
}

export interface EventExtraQuestion {
  idEventExtraQuestion: string
  question: string
  options: EventExtraQuestionOption[]
  answerType: ExtraQuestionAnswerType
  required: boolean
  active: boolean
  /** Quantidade de respostas já recebidas. Acima de 0, texto e opções ficam bloqueados. */
  answersCount: number
}

export interface SaveEventExtraQuestionPayload {
  question: string
  options: string[]
  answerType: ExtraQuestionAnswerType
  required: boolean
  active: boolean
}

export const EXTRA_QUESTION_RULES = {
  questionMaxLength: 120,
  optionMaxLength: 60,
  minOptions: 2,
  maxOptions: 8,
} as const

const reviewApi = () => APIDecoratorWithBaseURI()

export const useReviews = (eventId: string | undefined) => {
  const getEventReviews = useCallback(async (): Promise<EventReviewsResponse> => {
    const response = await reviewApi().get(`review/producer/event/${eventId}`)
    return response.data as EventReviewsResponse
  }, [eventId])

  const editEventReview = useCallback(
    async (idEventReview: string, payload: EditEventReviewPayload) => {
      const body: EditEventReviewPayload = {
        action: payload.action,
      }

      if (payload.action === 'rejected') {
        body.moderatedReason = payload.moderatedReason
      }

      const response = await reviewApi().put(
        `review/producer/event-review/${idEventReview}`,
        body
      )
      return response.data
    },
    []
  )

  const approveAllEventReviews = useCallback(async () => {
    const response = await reviewApi().put(
      `review/producer/event/${eventId}/approve-all-event-reviews`
    )
    return response.data
  }, [eventId])

  const getEventExtraQuestion =
    useCallback(async (): Promise<EventExtraQuestion | null> => {
      try {
        const response = await reviewApi().get(
          `review/producer/event/${eventId}/extra-question`
        )
        return (response.data?.data ?? response.data ?? null) as
          | EventExtraQuestion
          | null
      } catch (error: any) {
        if (error?.response?.status === 404) return null
        throw error
      }
    }, [eventId])

  const saveEventExtraQuestion = useCallback(
    async (
      payload: SaveEventExtraQuestionPayload,
      idEventExtraQuestion?: string
    ): Promise<EventExtraQuestion> => {
      const response = idEventExtraQuestion
        ? await reviewApi().put(
            `review/producer/event/${eventId}/extra-question/${idEventExtraQuestion}`,
            payload
          )
        : await reviewApi().post(
            `review/producer/event/${eventId}/extra-question`,
            payload
          )
      return (response.data?.data ?? response.data) as EventExtraQuestion
    },
    [eventId]
  )

  const setEventExtraQuestionActive = useCallback(
    async (idEventExtraQuestion: string, active: boolean) => {
      const response = await reviewApi().patch(
        `review/producer/event/${eventId}/extra-question/${idEventExtraQuestion}`,
        { active }
      )
      return response.data
    },
    [eventId]
  )

  return {
    getEventReviews,
    editEventReview,
    approveAllEventReviews,
    getEventExtraQuestion,
    saveEventExtraQuestion,
    setEventExtraQuestionActive,
  }
}
